import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../lib/api";
import { formatDateTime } from "../lib/dateFormat";
import {
  DIAGNOSTIC_KIND_LABELS,
  DIAGNOSTIC_STAGE_LABELS,
  listSuperadminDiagnostics,
  type DiagnosticKind,
  type DiagnosticListData,
} from "../lib/registrationDiagnostics";
import "../components/registrationDiagnostics.css";

const KINDS = Object.keys(DIAGNOSTIC_KIND_LABELS) as DiagnosticKind[];

/** Superadmin → Intentos fallidos de inscripción (Eduardo 2026-10-23). */
export default function SuperadminRegistrationDiagnosticsPage() {
  const { token } = useAuth();
  const [tournamentId, setTournamentId] = useState<number | null>(null);
  const [kind, setKind] = useState<DiagnosticKind | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [folioInput, setFolioInput] = useState("");
  const [folio, setFolio] = useState("");
  const [data, setData] = useState<DiagnosticListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setLoading(true);
    setError(null);
    listSuperadminDiagnostics(token, { tournamentId, kind, dateFrom, dateTo, folio, limit: 200 })
      .then((response) => {
        if (alive) setData(response.data);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof ApiError ? err.message : "No se pudo cargar la lista. Intenta otra vez.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [token, tournamentId, kind, dateFrom, dateTo, folio]);

  function searchFolio(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFolio(folioInput.trim().toUpperCase());
  }

  const allCount = data ? KINDS.reduce((sum, key) => sum + (data.counts[key] ?? 0), 0) : 0;

  return (
    <section className="card reg-diag">
      <div className="card__header">
        <div>
          <h1>Intentos fallidos de inscripción</h1>
          <p className="muted">
            Cada vez que la página de inscripción no pudo confirmar un envío, la familia vio un folio y aquí queda qué
            pasó. No se guarda nada del formulario.
          </p>
        </div>
        <Link className="btn btn--ghost btn--compact" to="/superadmin">
          Volver
        </Link>
      </div>

      <div className="reg-diag__filters">
        <label>
          <span>Torneo</span>
          <select
            value={tournamentId ?? ""}
            onChange={(event) => setTournamentId(event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">Todos</option>
            {(data?.tournaments ?? []).map((tournament) => (
              <option key={tournament.id} value={tournament.id}>
                {tournament.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Desde</span>
          <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
        </label>
        <label>
          <span>Hasta</span>
          <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
        </label>
        <form className="reg-diag__folio" onSubmit={searchFolio}>
          <label>
            <span>Folio</span>
            <input
              value={folioInput}
              maxLength={6}
              placeholder="ABC234"
              autoCapitalize="characters"
              onChange={(event) => setFolioInput(event.target.value)}
            />
          </label>
          <button type="submit" className="btn btn--compact">
            Buscar
          </button>
          {folio ? (
            <button
              type="button"
              className="btn btn--compact btn--ghost btn--tap44"
              onClick={() => {
                setFolio("");
                setFolioInput("");
              }}
            >
              Quitar
            </button>
          ) : null}
        </form>
      </div>

      <div className="reg-diag__kinds" role="group" aria-label="Filtrar por tipo">
        <button
          type="button"
          className={`btn btn--compact${kind === null ? "" : " btn--ghost"}`}
          aria-pressed={kind === null}
          onClick={() => setKind(null)}
        >
          Todos ({allCount})
        </button>
        {KINDS.map((key) => (
          <button
            key={key}
            type="button"
            className={`btn btn--compact${kind === key ? "" : " btn--ghost"}`}
            aria-pressed={kind === key}
            onClick={() => setKind(key)}
          >
            {DIAGNOSTIC_KIND_LABELS[key]} ({data?.counts[key] ?? 0})
          </button>
        ))}
      </div>

      {error ? (
        <p className="form-error" role="alert" data-no-toast>
          {error}
        </p>
      ) : null}
      {loading && !data ? <p className="muted">Cargando…</p> : null}
      {data && data.items.length === 0 ? <p className="muted">No hay intentos fallidos con estos filtros.</p> : null}
      {data && data.items.length > 0 ? (
        <ul className="reg-diag__list">
          {data.items.map((item) => (
            <li key={item.id} className="reg-diag__item">
              <div className="reg-diag__item-head">
                <strong className="reg-diag__folio-code">{item.folio}</strong>
                <span className={`reg-diag__pill reg-diag__pill--${item.error_kind}`}>
                  {DIAGNOSTIC_KIND_LABELS[item.error_kind] ?? item.error_kind}
                </span>
                <span className="muted">{formatDateTime(item.created_at)}</span>
              </div>
              <dl className="reg-diag__facts">
                <div>
                  <dt>Torneo</dt>
                  <dd>{item.tournament_name ?? item.tournament_slug}</dd>
                </div>
                <div>
                  <dt>Etapa</dt>
                  <dd>{DIAGNOSTIC_STAGE_LABELS[item.stage] ?? item.stage}</dd>
                </div>
                <div>
                  <dt>Código</dt>
                  <dd>{item.http_status ?? "—"}</dd>
                </div>
                <div>
                  <dt>Internet</dt>
                  <dd>{item.online === null ? "—" : item.online ? "Sí" : "No"}</dd>
                </div>
                <div>
                  <dt>Equipo</dt>
                  <dd>{[item.browser, item.os].filter(Boolean).join(" · ") || "—"}</dd>
                </div>
                <div>
                  <dt>Cuenta</dt>
                  <dd>{item.user_id ? `#${item.user_id}` : "Sin sesión"}</dd>
                </div>
              </dl>
              {item.error_code ? <p className="reg-diag__message">Código: {item.error_code}</p> : null}
              <p className="muted reg-diag__version">Versión: {item.app_version ?? "—"}</p>
            </li>
          ))}
        </ul>
      ) : null}
      {data && data.total > data.items.length ? (
        <p className="muted">
          Mostrando {data.items.length} de {data.total}. Usa los filtros para acotar.
        </p>
      ) : null}
    </section>
  );
}
